'use client';
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { CheckIcon } from '@heroicons/react/20/solid';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { useDebouncedCallback } from 'use-debounce';
const unionSets = (setA, setB) => {
    const result = new Set(setA);
    setB.forEach((value) => result.add(value));
    return result;
};

const intersectSets = (sets, fallback) => {
    if (!sets || sets.length === 0) {
        return new Set(fallback);
    }
    const [first, ...rest] = sets;
    const result = new Set(first);
    rest.forEach((current) => {
        if (!current) {
            return;
        }
        Array.from(result).forEach((value) => {
            if (!current.has(value)) {
                result.delete(value);
            }
        });
    });
    return result;
};

const hasIntersection = (setA, setB) => {
    if (!setA || !setB) {
        return false;
    }
    const [smaller, larger] = setA.size <= setB.size ? [setA, setB] : [setB, setA];
    for (const value of smaller) {
        if (larger.has(value)) {
            return true;
        }
    }
    return false;
};

export default function FilterItems({ section }) {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { replace } = useRouter();

    const filtersData = useMemo(() => (Array.isArray(section?.filters) ? section.filters : []), [section]);

    const valueProductMap = useMemo(() => {
        const map = new Map();
        filtersData.forEach((filter) => {
            const valueMap = new Map();
            const values = Array.isArray(filter?.VALUES) ? filter.VALUES : [];
            values.forEach((value) => {
                const valueId = String(value?.ID ?? value?.VALUE ?? '');
                if (!valueId) {
                    return;
                }
                const productIdsArray = Array.isArray(value?.PRODUCT_IDS) ? value.PRODUCT_IDS : [];
                const productIdSet = new Set(productIdsArray.map((id) => String(id)));
                valueMap.set(valueId, productIdSet);
            });
            map.set(filter.CODE, valueMap);
        });
        return map;
    }, [filtersData]);

    const allProductIds = useMemo(() => {
        const result = new Set();
        valueProductMap.forEach((valueMap) => {
            valueMap.forEach((productSet) => {
                productSet.forEach((id) => result.add(id));
            });
        });
        return result;
    }, [valueProductMap]);

    // Состояние для фильтров и фильтрации для каждой группы
    const getInitialFilters = useCallback(() => {
        const initialFilters = {};
        filtersData.forEach((filter) => {
            const paramValue = searchParams.get(filter.CODE);
            initialFilters[filter.CODE] = paramValue ? paramValue.split(",").filter(Boolean) : [];
        });
        return initialFilters;
    }, [filtersData, searchParams]);

    const getInitialSearchTerms = useCallback(() => {
        const initialSearchTerms = {};
        filtersData.forEach((filter) => {
            initialSearchTerms[filter.CODE] = "";
        });
        return initialSearchTerms;
    }, [filtersData]);

    const [filters, setFilters] = useState(() => getInitialFilters());
    const [searchTerms, setSearchTerms] = useState(() => getInitialSearchTerms());
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());

    // Функция для парсинга URL параметров
    const handleSearch = useDebouncedCallback((updatedFilters) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', '1');

        Object.keys(updatedFilters).forEach((key) => {
            const values = updatedFilters[key];
            if (values.length > 0) {
                params.set(key, values.join(","));
            } else {
                params.delete(key);
            }
        });

        replace(`${pathname}?${params.toString()}`, { scroll: false });
    }, 10);

    const toggleFilterValue = (filterKey, valueId) => {
        setFilters((prev) => {
            const current = Array.isArray(prev?.[filterKey]) ? prev[filterKey] : [];
            const normalizedValue = String(valueId);
            const exists = current.includes(normalizedValue);
            const nextValues = exists
                ? current.filter((item) => item !== normalizedValue)
                : [...current, normalizedValue];

            const updatedFilters = {
                ...prev,
                [filterKey]: nextValues,
            };

            handleSearch(updatedFilters);
            return updatedFilters;
        });
    };

    const toggleGroupExpand = (code) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            return next;
        });
    };

    // Обновляем фильтры при изменении URL
    useEffect(() => {
        setFilters(getInitialFilters());
    }, [searchParams, getInitialFilters]);

    useEffect(() => {
        setSearchTerms(getInitialSearchTerms());
    }, [getInitialSearchTerms]);

    const matchingByFilter = useMemo(() => {
        const map = new Map();
        filtersData.forEach((filter) => {
            const selectedValues = (filters?.[filter.CODE] || []).filter(Boolean);
            if (selectedValues.length === 0) {
                map.set(filter.CODE, new Set(allProductIds));
                return;
            }

            const valueMap = valueProductMap.get(filter.CODE);
            let union = null;
            selectedValues.forEach((valueId) => {
                const valueSet = valueMap?.get(String(valueId));
                if (!valueSet) {
                    return;
                }
                union = union ? unionSets(union, valueSet) : new Set(valueSet);
            });

            map.set(filter.CODE, union || new Set());
        });
        return map;
    }, [filtersData, filters, valueProductMap, allProductIds]);

    // Фильтрация значений чекбоксов на основе строки поиска для конкретной группы
    const filteredValues = (filter) => {
        const searchTerm = (searchTerms?.[filter.CODE] || '').toLowerCase();
        const values = Array.isArray(filter?.VALUES) ? filter.VALUES : [];
        return values.filter((value) => {
            const label = String(value?.VALUE ?? value?.ID ?? '').toLowerCase();
            return label.includes(searchTerm);
        });
    };

    // Обновление строки поиска для конкретной группы
    const handleSearchTermChange = (filterKey, value) => {
        setSearchTerms({
            ...searchTerms,
            [filterKey]: value // Обновляем строку поиска для конкретного фильтра
        });
    };

    const renderValueItem = (filter, value) => {
        const valueId = String(value.ID ?? value.VALUE ?? '');
        if (!valueId) {
            return null;
        }

        const valueLabel = String(value.VALUE ?? value.ID ?? '');
        const isSelected = (filters?.[filter.CODE] || []).includes(valueId);
        const otherFilterSets = filtersData
            .filter((other) => other.CODE !== filter.CODE)
            .map((other) => matchingByFilter.get(other.CODE))
            .filter((set) => set !== undefined);
        const availableBaseSet = intersectSets(otherFilterSets, allProductIds);
        const valueSet = valueProductMap.get(filter.CODE)?.get(valueId) || new Set();
        const isAvailable = hasIntersection(valueSet, availableBaseSet);
        const isDisabled = !isAvailable && !isSelected;

        const baseClasses = 'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors';
        const enabledClasses = isSelected
            ? 'text-primary'
            : 'text-default-700 hover:text-primary';
        const disabledClasses = 'text-default-400 cursor-not-allowed';

        return (
            <label
                key={valueId}
                className={`relative ${baseClasses} ${isDisabled ? disabledClasses : enabledClasses}`}
            >
                <input
                    type="checkbox"
                    className="sr-only"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => toggleFilterValue(filter.CODE, valueId)}
                />
                <span
                    className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                        isDisabled
                            ? 'border-default-200 bg-default-100 text-default-300'
                            : isSelected
                                ? 'border-primary bg-primary text-white'
                                : 'border-default-300 bg-white text-transparent'
                    }`}
                >
                    <CheckIcon className="h-3 w-3" />
                </span>
                <span className="flex-1 text-left leading-tight">
                    {valueLabel}
                </span>
            </label>
        );
    };

    return (
        <div className="flex flex-col gap-4 mt-4">
            {filtersData.length > 0 && filtersData.map((filter) => {
                const values = filteredValues(filter);
                const showSearch = Array.isArray(filter?.VALUES) && filter.VALUES.length > 10;
                const isExpanded = expandedGroups.has(filter.CODE);
                const displayValues = isExpanded ? values : values.slice(0, 5);
                const showToggle = values.length > 5;

                return (
                    <div key={filter.CODE} className='space-y-3'>
                        <div className='text-medium font-medium leading-8'>{filter.NAME}</div>
                        {showSearch && (
                            <div className="relative mb-1">
                                <Input
                                    size={'sm'}
                                    placeholder={`Фильтровать ${filter.NAME}`}
                                    value={searchTerms?.[filter.CODE] ?? ''}
                                    onChange={(e) => handleSearchTermChange(filter.CODE, e.target.value)}
                                    className="pr-8"
                                />
                                {searchTerms?.[filter.CODE] && (
                                    <button
                                        type="button"
                                        onClick={() => handleSearchTermChange(filter.CODE, "")}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col gap-1">
                            {displayValues.map((value) => renderValueItem(filter, value))}
                        </div>

                        {showToggle && (
                            <button
                                type="button"
                                className="text-sm font-semibold text-primary hover:underline"
                                onClick={() => toggleGroupExpand(filter.CODE)}
                            >
                                {isExpanded ? 'Свернуть' : 'Посмотреть все'}
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
